import { useCallback, useRef } from "react";

type Pending = { pageIndex: number; dataUrl: string } | null;

type UseAddImagePageArgs = {
  basePageCount: number;
  extraPages: number;
  setExtraPages: React.Dispatch<React.SetStateAction<number>>;
  setActivePage?: (pageIndex: number) => void;
  pushState: (pageIndex: number) => void;
  canvasRefs: React.MutableRefObject<Record<number, HTMLCanvasElement | null>>;
};

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = dataUrl;
  });
}

export const useAddImage = ({
  basePageCount,
  extraPages,
  setExtraPages,
  setActivePage,
  pushState,
  canvasRefs,
}: UseAddImagePageArgs) => {
  const pendingRef = useRef<Pending>(null);

  const drawImageToCanvas = useCallback(
    async (canvas: HTMLCanvasElement, dataUrl: string) => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const img = await loadImage(dataUrl);
      const dpr = window.devicePixelRatio || 1;

      // ✅ Gebruik dezelfde breedte als je PDF canvas (via clientWidth),
      // maar bepaal hoogte uit aspect ratio van de foto
      const cssW = canvas.clientWidth || 800;
      const aspect = img.height / img.width;
      const cssH = Math.max(1, Math.round(cssW * aspect));

      // ✅ Zet ook de CSS hoogte, zodat de "page" visueel kleiner/groter wordt
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;

      // ✅ Echte buffer size (HiDPI)
      const targetW = Math.floor(cssW * dpr);
      const targetH = Math.floor(cssH * dpr);

      if (canvas.width !== targetW || canvas.height !== targetH) {
        canvas.width = targetW;
        canvas.height = targetH;
      }

      // ✅ Teken in CSS pixels
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      // contain-fit in de nieuwe pagina (past altijd)
      const scale = Math.min(cssW / img.width, cssH / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      const dx = (cssW - dw) / 2;
      const dy = (cssH - dh) / 2;

      ctx.drawImage(img, dx, dy, dw, dh);
    },
    []
  );

  const queueAddImagePage = useCallback(
    (dataUrl: string) => {
      const newPageIndex = basePageCount + extraPages;

      pendingRef.current = { pageIndex: newPageIndex, dataUrl };

      setExtraPages((p) => p + 1);
      setActivePage?.(newPageIndex);

      return newPageIndex;
    },
    [basePageCount, extraPages, setExtraPages, setActivePage]
  );

  const onCanvasReady = useCallback(
    async (pageIndex: number, canvas: HTMLCanvasElement | null) => {
      if (!canvas) return;
      canvasRefs.current[pageIndex] = canvas;

      const pending = pendingRef.current;
      if (!pending || pending.pageIndex !== pageIndex) return;

      try {
        await drawImageToCanvas(canvas, pending.dataUrl);

        // ✅ pushState pas NA tekenen
        pushState(pageIndex);
      } finally {
        pendingRef.current = null;
      }
    },
    [canvasRefs, drawImageToCanvas, pushState]
  );

  const hasPending = useCallback(() => pendingRef.current !== null, []);

  return { queueAddImagePage, onCanvasReady, hasPending };
};